import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { FitBadge, ScoreBars, ScoreRadar } from '../../components/hikari/ScoreBreakdown';
import { CustomQuestionsEditor } from '../../components/hikari/CustomQuestionsEditor';
import { toast } from 'sonner';
import { ShieldCheck, MapPin, User, ChatCircleText } from '@phosphor-icons/react';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);         // loaded instantly via /jobs/{id}
  const [data, setData] = useState(null);       // ranked candidates (slow LLM call)
  const [selected, setSelected] = useState(null);
  const [rankedLoading, setRankedLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Fast first paint: fetch the job itself immediately.
  useEffect(() => {
    api.get(`/jobs/${id}`)
      .then((r) => setJob(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      });
  }, [id]);

  // Slower: ranked candidates (triggers LLM scoring). Don't block the page.
  useEffect(() => {
    api.get(`/employer/jobs/${id}/ranked`)
      .then((r) => {
        setData(r.data);
        if (r.data.ranked.length) setSelected(r.data.ranked[0]);
      })
      .catch(() => {})
      .finally(() => setRankedLoading(false));
  }, [id]);

  const invite = async (candidate_id) => {
    try {
      await api.post(`/employer/applications/${candidate_id}/${id}/action`, { action: 'invite' });
      toast.success('Candidate invited — take the next step from their profile.');
      navigate(`/employer/candidate/${candidate_id}?job=${id}`);
    } catch {
      toast.error('Invite failed');
    }
  };

  if (notFound) {
    return (
      <Shell>
        <div className="max-w-[800px] mx-auto px-6 py-20 text-center" data-testid="job-not-found">
          <div className="label-eyebrow text-primary mb-2">404</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-4">Job not found</h1>
          <p className="text-muted-foreground mb-6">The job you're looking for doesn't exist or was removed.</p>
          <Button className="rounded-none" onClick={() => window.location.assign('/employer/jobs')} data-testid="back-to-jobs">
            Back to jobs
          </Button>
        </div>
      </Shell>
    );
  }

  if (!job) {
    return <Shell><div className="p-8 mono text-sm">Loading job...</div></Shell>;
  }

  return (
    <Shell>
      <div className="max-w-[1500px] mx-auto px-6 py-10">
        <div className="label-eyebrow text-primary mb-2">{(job.source || 'manual').toUpperCase()}</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-2">{job.title}</h1>
        <div className="flex items-center gap-3 text-sm text-muted-foreground mb-6">
          <MapPin size={14} /> {job.location || '—'} · {job.country}
        </div>
        <p className="max-w-3xl text-sm text-muted-foreground mb-4 leading-relaxed">{job.description}</p>
        <div className="flex flex-wrap gap-1 mb-8">
          {(job.required_skills || []).map((s) => (
            <span key={s} className="label-eyebrow bg-secondary px-2 py-0.5">{s}</span>
          ))}
        </div>

        {/* Employer-only: attach job-specific questions candidates must answer on apply */}
        <CustomQuestionsEditor jobId={job.id} initialQuestions={job.custom_questions || []} roleFamilyId={job.role_family_id || null} />

        <div className="grid lg:grid-cols-12 gap-0 grid-skeleton">
          {/* Ranked list */}
          <div className="lg:col-span-5 bg-white" data-testid="ranked-list">
            <div className="p-4 border-b border-border bg-secondary">
              <div className="label-eyebrow">
                RANKED CANDIDATES {data ? `· ${data.ranked.length}` : ''}
              </div>
            </div>
            {rankedLoading ? (
              <div className="p-6 text-sm text-muted-foreground mono">
                Scoring candidates… this takes a few seconds on first load.
              </div>
            ) : !data || data.ranked.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">
                No candidates have completed assessments yet for this role family.
              </div>
            ) : (
              data.ranked.map((r, idx) => (
                <button
                  key={r.candidate.user_id}
                  onClick={() => setSelected(r)}
                  className={`w-full text-left p-4 border-b border-border flex items-center gap-4 transition-colors ${selected?.candidate.user_id === r.candidate.user_id ? 'bg-foreground text-background' : 'hover:bg-secondary/30'}`}
                  data-testid={`rank-row-${idx}`}
                >
                  <div className="mono font-black text-xl w-8 text-center">{idx + 1}</div>
                  <FitBadge value={r.score.overall_fit} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{r.candidate.name}</div>
                    <div className="mono text-xs opacity-70 truncate">{r.candidate.headline || r.candidate.email}</div>
                  </div>
                  <div className="flex flex-col items-end text-xs">
                    {r.score.explanation && <span className="label-eyebrow opacity-60">+ AI EXPL</span>}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-7 bg-white p-6" data-testid="candidate-detail">
            {!selected ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                {rankedLoading ? 'Loading candidate breakdown…' : 'Select a candidate.'}
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 label-eyebrow text-muted-foreground">
                      <User size={14} /> CANDIDATE
                    </div>
                    <h2 className="font-display font-black text-3xl tracking-tighter mt-1">{selected.candidate.name}</h2>
                    <div className="text-sm text-muted-foreground">{selected.candidate.headline || selected.candidate.email}</div>
                    <div className="mono text-xs text-muted-foreground mt-1">
                      {selected.candidate.location || '—'} · {selected.candidate.country} · {selected.candidate.work_authorization}
                    </div>
                  </div>
                  <FitBadge value={selected.score.overall_fit} size="lg" />
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <div className="label-eyebrow text-muted-foreground mb-2">RADAR</div>
                    <ScoreRadar scores={selected.score} />
                  </div>
                  <div>
                    <div className="label-eyebrow text-muted-foreground mb-2">BREAKDOWN</div>
                    <ScoreBars scores={selected.score} />
                    <div className="mono text-xs text-muted-foreground mt-4">
                      Attempts considered: {selected.score.attempts_considered}
                    </div>
                  </div>
                </div>

                <div className="mt-6 border-t border-border pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck size={16} className="text-primary" />
                    <div className="label-eyebrow">AI EXPLANATION</div>
                  </div>
                  <p className="text-sm leading-relaxed">{selected.score.explanation}</p>
                </div>

                {selected.score.work_style_summary && (
                  <div className="mt-5">
                    <div className="label-eyebrow text-muted-foreground mb-2">WORK-STYLE</div>
                    <p className="text-sm leading-relaxed italic">{selected.score.work_style_summary}</p>
                  </div>
                )}

                <div className="mt-8 flex flex-wrap gap-2">
                  <Link to={`/employer/candidate/${selected.candidate.user_id}?job=${id}`} data-testid="action-view-profile">
                    <Button variant="outline" className="rounded-none">View full profile</Button>
                  </Link>
                  <Button onClick={() => invite(selected.candidate.user_id)} className="rounded-none" data-testid="action-invite">
                    Invite &amp; open profile →
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default JobDetail;
